import { faBalanceScale, faExchange, faFileExport, faMoon, faSun, faBrain } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputNumber, Modal, Select, Tooltip } from "antd";
import _ from "lodash";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { receiveTrackGeomData } from "redux/actions";
import { ReactComponent as DetailRailIcon } from "../../display/image/RailButtons/DetailRailIcon.svg";
import { mlDetectionColors } from "./series";

const { Option } = Select;

const showProfilePointsSelector = (state) => state.trackGeometry.showProfilePoints;

const ChartFilters = memo(
    ({
        title,
        selectedDataTypes,
        handleSelectedDataTypes,
        geomHeaders,
        exceedenceSpeed,
        setExceedenceSpeed,
        colourScheme,
        handleColourScheme,
        userIsAdmin,
        userConfig,
        setShowCompareConfig,
        setShowExportConfig,
        hasLeftRightAlignedData,
        alignToTrackLeftRight,
        handleAlignToTrackLeftRight,
        geometryData,
        geomDataFiles,
        comparisonSession,
        comparisonOffset,
        setComparisonOffset,
        verticalComparisonOffset,
        setVerticalComparisonOffset,
        showPoints,
        handleShowPoints,
        showMLDetections,
        handleShowMLDetections,
    }) => {
        const dispatch = useDispatch();
        const allowedToShowPoints = useSelector(showProfilePointsSelector);

        const [comparisonModalOpen, setComparisonModalOpen] = useState(false);

        const resetTrackGeometryDataLoadedMarkers = () => {
            const _geometryData = [...geometryData];
            let index = 0;
            for (let i = 0; i < geomDataFiles.length; i++) {
                _geometryData[index].loaded = false;
                index += geomDataFiles[i].record_count;
            }
            dispatch(receiveTrackGeomData(_geometryData));
        };

        const orderedDataTypes = useMemo(() => {
            return _.chain(selectedDataTypes)
                .map((t) => geomHeaders.find((h) => h.header_name === t))
                .sortBy((h) => (h.info.order !== null ? h.info.order : h.name))
                .map((h) => h.header_name)
                .value();
        }, [selectedDataTypes, geomHeaders]);

        const ComparisonTitle = useCallback(() => {
            if (!comparisonSession) return null;

            const title = _.get(comparisonSession, ["names", 0], "unknown");

            return <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>{title}</div>;
        }, [comparisonSession]);

        const ComparisonAdjustment = useCallback(
            () => (
                <div
                    className="flex justify-center items-center"
                    style={{ gap: 8 }}>
                    <div
                        className="flex justify-center items-center"
                        style={{ gap: 4 }}>
                        <span>Horizontal adjustment</span>
                        <InputNumber
                            value={comparisonOffset}
                            onChange={setComparisonOffset}
                            min={-25}
                            max={25}
                            step={0.2}
                            formatter={(v) => `${v}m`}
                            parser={(v) => v.replace(/[^-.0-9]/g, "")}
                        />
                    </div>
                    <div
                        className="flex justify-center items-center"
                        style={{ gap: 4 }}>
                        <span>Vertical adjustment (divs)</span>
                        <InputNumber
                            value={verticalComparisonOffset}
                            onChange={setVerticalComparisonOffset}
                            min={-25}
                            max={25}
                            step={0.05}
                            formatter={(v) => `${v}`}
                            parser={(v) => v.replace(/[^-.0-9]/g, "")}
                        />
                    </div>
                </div>
            ),

            [comparisonOffset, setComparisonOffset, verticalComparisonOffset, setVerticalComparisonOffset],
        );

        return (
            <>
                <div className="flex text-no-wrap w-full bg-card gap-4 p-8 rounded-8 items-center justify-between">
                    <div>
                        <span
                            className="px-4 "
                            style={{ fontSize: 26, alignSelf: "flex-end" }}>
                            {title}
                        </span>
                    </div>
                    <div className="flex w-full flex-1 gap-8 justify-end">
                        <div
                            style={{ minWidth: 720 }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}>
                            <Select
                                className="GeometryToolbarSelect"
                                onChange={handleSelectedDataTypes}
                                value={orderedDataTypes}
                                mode="multiple"
                                showSearch
                                allowClear
                                placeholder="Please select"
                                optionFilterProp="children"
                                //can't do max tag count responsive in antd3 :(
                            >
                                {geomHeaders &&
                                    _.chain(geomHeaders)
                                        .filter((option) => option.name)
                                        .filter((h) => !_.isEmpty(h.info))
                                        .sortBy((h) => (h.info.order !== null ? h.info.order : h.name))
                                        .map((option, index) => (
                                            <Option
                                                key={index}
                                                value={option.header_name}
                                                onClick={(e) => e.domEvent.stopPropagation()}>
                                                {_.get(option, "info.display_name", option.name)}
                                            </Option>
                                        ))
                                        .value()}
                            </Select>
                        </div>

                        <Select
                            value={exceedenceSpeed}
                            onChange={setExceedenceSpeed}
                            className="GeometryToolbarSelect"
                            style={{ width: 140 }}>
                            <Option value="none">Don't Show</Option>
                            <Option value="line">For line speed</Option>
                            <Option value={25}>Up to 25 mph</Option>
                            <Option value={50}>26-50 mph</Option>
                            <Option value={75}>51-75 mph</Option>
                            <Option value={100}>76-100 mph</Option>
                            <Option value={125}>101-125 mph</Option>
                        </Select>

                        <div className="flex gap-8 items-center">
                            <Tooltip title={!hasLeftRightAlignedData ? "This session has no left-right aligned geometry" : "Align to track left and right"}>
                                <div
                                    className={`GeometryToolbarButton ${alignToTrackLeftRight && "active "} ${!hasLeftRightAlignedData && "disabled "}`}
                                    onClick={() => {
                                        handleAlignToTrackLeftRight(!alignToTrackLeftRight);
                                        resetTrackGeometryDataLoadedMarkers();
                                    }}>
                                    <FontAwesomeIcon
                                        icon={faExchange}
                                        size="lg"
                                    />
                                </div>
                            </Tooltip>
                            {allowedToShowPoints && (
                                <Tooltip title={"Show rail profile points"}>
                                    <div
                                        className={`GeometryToolbarButton ${showPoints && "active"}`}
                                        onClick={() => {
                                            handleShowPoints(!showPoints);
                                        }}>
                                        <DetailRailIcon />
                                    </div>
                                </Tooltip>
                            )}

                            <Tooltip
                                title={
                                    <div
                                        className="flex flex-col"
                                        style={{ gap: "8px" }}>
                                        <span>Show Track Features</span>
                                        <div
                                            className="flex flex-col items-start"
                                            style={{ gap: "12px", maxWidth: "300px" }}>
                                            {Object.entries(mlDetectionColors)
                                                .filter(([key]) => !key.startsWith("spare"))
                                                .map(([key, color]) => (
                                                    <div
                                                        key={key}
                                                        className="flex items-center"
                                                        style={{ gap: "6px" }}>
                                                        <div
                                                            style={{ width: 10, height: 10, backgroundColor: color, transform: "rotate(45deg)", flexShrink: 0 }}
                                                        />
                                                        <span className="text-xs whitespace-nowrap">
                                                            {key
                                                                .split("_")
                                                                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                                                .join(" ")}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                }>
                                <div
                                    className={`GeometryToolbarButton ${showMLDetections && "active"}`}
                                    onClick={() => {
                                        handleShowMLDetections(!showMLDetections);
                                    }}>
                                    <FontAwesomeIcon
                                        icon={faBrain}
                                        size="lg"
                                    />
                                </div>
                            </Tooltip>

                            <Tooltip title="Export">
                                <div
                                    className="GeometryToolbarButton"
                                    onClick={() => setShowExportConfig(true)}>
                                    <FontAwesomeIcon
                                        icon={faFileExport}
                                        size="lg"
                                    />
                                </div>
                            </Tooltip>

                            {(!!userIsAdmin || !!userConfig["super_admin"]) && (
                                <>
                                    <Tooltip title="Compare">
                                        <div
                                            className={`GeometryToolbarButton ${comparisonSession && "active "}`}
                                            onClick={() => setShowCompareConfig(true)}>
                                            <FontAwesomeIcon
                                                icon={faBalanceScale}
                                                size="lg"
                                            />
                                            {/* comparison modal to be inlined on main chart */}
                                        </div>
                                    </Tooltip>
                                    <Tooltip title={`Switch to ${colourScheme === "dark" ? "Light Theme" : "Dark Theme"} `}>
                                        <div
                                            className="GeometryToolbarButton"
                                            onClick={() => handleColourScheme(colourScheme === "dark" ? "light" : "dark")}>
                                            <FontAwesomeIcon
                                                icon={colourScheme === "dark" ? faSun : faMoon}
                                                size="lg"
                                            />
                                        </div>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <Modal
                    width={600}
                    title={<ComparisonTitle />}
                    visible={comparisonModalOpen && comparisonSession !== null}
                    onCancel={() => setComparisonModalOpen(false)}
                    footer={null}
                    destroyOnClose>
                    <ComparisonAdjustment />
                </Modal>
            </>
        );
    },
);

export default ChartFilters;
